import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { forexApi, type ConfigPackage, type ConfigRelease } from "@/lib/forex-api";

type Toast = { type: "success" | "error"; msg: string };

const supportedComplianceKeys = ["app_terms", "app_compliance_copy"];

function formatTime(timestamp?: number | null) {
  if (!timestamp) return "未发布";
  return new Date(timestamp * 1000).toLocaleString("sv-SE").slice(0, 19);
}

function normalizeConfigKey(raw: string | null) {
  if (raw && supportedComplianceKeys.includes(raw)) return raw;
  if (raw === "DOC-001" || raw === "risk_disclosure") return "app_compliance_copy";
  return "app_terms";
}

function metadata(configKey: string) {
  if (configKey === "app_terms") {
    return {
      typeLabel: "用户协议",
      scope: "所有 C 端注册与登录用户",
      scenario: ["注册登录", "风险协议确认", "个人中心协议入口"],
      chip: "bg-brand/10 text-brand",
      icon: "ri-file-text-line",
    };
  }
  return {
    typeLabel: "风险披露",
    scope: "所有开启外汇跟单的 C 端用户",
    scenario: ["绑定 MT4 子账号", "跟单启用", "风险确认"],
    chip: "bg-red-500/10 text-red-500",
    icon: "ri-alert-line",
  };
}

export default function ComplianceDetailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const configKey = normalizeConfigKey(searchParams.get("type") || searchParams.get("id"));
  const [pkg, setPkg] = useState<ConfigPackage | null>(null);
  const [releases, setReleases] = useState<ConfigRelease[]>([]);
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [rolling, setRolling] = useState("");
  const [toast, setToast] = useState<Toast | null>(null);

  const loadDoc = useCallback(() => {
    setLoading(true);
    Promise.all([forexApi.getConfigPackage(configKey), forexApi.listConfigReleases()])
      .then(([packageResult, releaseResult]) => {
        setPkg(packageResult);
        setDraft(JSON.stringify(packageResult.payload, null, 2));
        setReleases(releaseResult.items.filter((item) => item.config_key === configKey));
      })
      .catch(() => setToast({ type: "error", msg: "协议与合规详情加载失败，请确认后端服务或登录态。" }))
      .finally(() => setLoading(false));
  }, [configKey]);

  useEffect(() => {
    loadDoc();
  }, [loadDoc]);

  const parseDraft = () => {
    try {
      return JSON.parse(draft) as Record<string, unknown>;
    } catch {
      setToast({ type: "error", msg: "JSON 格式错误，请修正后再保存。" });
      return null;
    }
  };

  const handleSaveDraft = async () => {
    if (!pkg) return;
    const payload = parseDraft();
    if (!payload) return;
    setSaving(true);
    try {
      const updated = await forexApi.patchConfigPackage(pkg.config_key, {
        payload,
        description: "协议与合规详情页编辑",
      });
      setPkg(updated);
      setDraft(JSON.stringify(updated.payload, null, 2));
      setEditing(false);
      setToast({ type: "success", msg: "协议与合规草稿已保存" });
    } catch {
      setToast({ type: "error", msg: "协议与合规保存失败" });
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!pkg) return;
    setPublishing(true);
    try {
      const result = await forexApi.publishConfigPackage(pkg.config_key, {
        description: "协议与合规后台发布记录",
      });
      setPkg(result.package);
      setDraft(JSON.stringify(result.package.payload, null, 2));
      setReleases((prev) => [result.release, ...prev]);
      setToast({ type: "success", msg: "后台发布记录已生成；C 端实际读取结果待核对。" });
    } catch {
      setToast({ type: "error", msg: "协议与合规发布失败" });
    } finally {
      setPublishing(false);
    }
  };

  const handleRollback = async (releaseId: string) => {
    if (!pkg) return;
    setRolling(releaseId);
    try {
      const result = await forexApi.rollbackConfigPackage(pkg.config_key, releaseId);
      setPkg(result.package);
      setDraft(JSON.stringify(result.package.payload, null, 2));
      setReleases((prev) => [result.release, ...prev.filter((item) => item.release_id !== result.release.release_id)]);
      setToast({ type: "success", msg: "协议与合规已回滚并生成记录" });
    } catch {
      setToast({ type: "error", msg: "协议与合规回滚失败" });
    } finally {
      setRolling("");
    }
  };

  const meta = metadata(configKey);
  const fallbackRelease = releases.find((item) => item.status !== "active") || releases[1];

  return (
    <div className="space-y-5">
      <Breadcrumb items={[{ label: "管理后台", href: "/forex/overview" }, { label: "风控与配置", href: "/forex/compliance" }, { label: "协议详情" }]} />

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-lg font-semibold text-text-primary">{pkg?.label || meta.typeLabel}</h2>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${pkg?.status === "enabled" ? "bg-emerald-500/10 text-emerald-500" : "bg-text-muted/10 text-text-secondary"}`}>
              <i className={`text-[8px] ${pkg?.status === "enabled" ? "ri-checkbox-circle-fill" : "ri-pause-circle-fill"}`} />
              {pkg?.status === "enabled" ? "已启用" : "未启用"}
            </span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${meta.chip}`}>
              <i className={`${meta.icon} text-[10px]`} />{meta.typeLabel}
            </span>
            <span className="text-[10px] text-text-muted font-mono">{configKey}</span>
          </div>
          <p className="text-xs text-text-muted mt-0.5">{pkg?.description || "协议与合规配置"} · {pkg?.api_path || "GET /app/config/bootstrap"}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setEditing((prev) => !prev)} disabled={!pkg} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-brand/10 text-brand hover:bg-brand/20 disabled:opacity-50">
            <i className={editing ? "ri-close-line text-sm" : "ri-edit-line text-sm"} />{editing ? "取消编辑" : "编辑"}
          </button>
          <button onClick={handleSaveDraft} disabled={!editing || saving} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-brand text-white disabled:opacity-50">
            <i className={saving ? "ri-loader-4-line animate-spin text-sm" : "ri-save-line text-sm"} />{saving ? "保存中" : "保存草稿"}
          </button>
          <button onClick={handlePublish} disabled={!pkg || publishing} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-500 text-white disabled:opacity-50">
            <i className={publishing ? "ri-loader-4-line animate-spin text-sm" : "ri-upload-cloud-line text-sm"} />{publishing ? "发布中" : "生成发布记录"}
          </button>
          <button onClick={() => navigate(`/forex/config/releases?type=${configKey}`)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand">
            <i className="ri-file-list-3-line text-sm" />发布记录
          </button>
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand">
            <i className="ri-arrow-left-line text-sm" />返回
          </button>
        </div>
      </div>

      {toast && (
        <div className={`rounded-lg border px-4 py-3 text-sm ${toast.type === "success" ? "border-emerald-500/20 bg-emerald-500/5 text-emerald-600" : "border-red-500/20 bg-red-500/5 text-red-500"}`}>
          {toast.msg}
        </div>
      )}

      {loading && <div className="bg-white rounded-lg border border-surface-border px-4 py-10 text-center text-sm text-text-muted">加载中...</div>}

      {!loading && pkg && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg border border-surface-border p-4">
              <p className="text-[11px] text-text-muted">版本</p>
              <p className="text-lg font-semibold text-text-primary font-mono">{pkg.version}</p>
            </div>
            <div className="bg-white rounded-lg border border-surface-border p-4">
              <p className="text-[11px] text-text-muted">最后发布</p>
              <p className="text-sm font-semibold text-text-primary font-mono mt-1">{formatTime(pkg.last_published_at)}</p>
            </div>
            <div className="bg-white rounded-lg border border-surface-border p-4">
              <p className="text-[11px] text-text-muted">发布记录</p>
              <p className="text-lg font-semibold text-brand font-mono">{releases.length}</p>
            </div>
            <div className="bg-white rounded-lg border border-surface-border p-4">
              <p className="text-[11px] text-text-muted">影响 API</p>
              <p className="text-sm font-semibold text-brand font-mono mt-1">{pkg.api_path}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg border border-surface-border p-4 lg:col-span-2 space-y-4">
              <h3 className="text-sm font-semibold text-text-primary">协议与合规配置</h3>
              <div className="grid grid-cols-2 gap-2.5">
                {[
                  { label: "配置键", value: pkg.config_key, mono: true },
                  { label: "状态", value: pkg.status === "enabled" ? "已启用" : "未启用" },
                  { label: "类型", value: meta.typeLabel },
                  { label: "生效范围", value: meta.scope },
                ].map((row) => (
                  <div key={row.label} className="flex justify-between items-center py-2 px-3 bg-surface/50 rounded-md">
                    <span className="text-xs text-text-muted">{row.label}</span>
                    <span className={`text-xs text-text-primary font-medium ${row.mono ? "font-mono" : ""}`}>{row.value}</span>
                  </div>
                ))}
              </div>

              <div>
                <span className="text-xs text-text-muted font-medium">绑定场景</span>
                <div className="flex flex-wrap gap-2 mt-2">
                  {meta.scenario.map((scenario) => (
                    <span key={scenario} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium bg-brand/5 text-brand border border-brand/10">
                      <i className="ri-link text-xs" />{scenario}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <span className="text-xs text-text-muted font-medium">配置 JSON</span>
                  <span className="text-[10px] text-text-muted font-mono">{pkg.api_path} {"->"} {pkg.config_key}</span>
                </div>
                <textarea
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  readOnly={!editing}
                  className={`w-full min-h-[360px] rounded-lg border border-surface-border px-3 py-2 text-xs font-mono focus:border-brand/50 focus:outline-none ${editing ? "bg-white text-text-primary" : "bg-surface/50 text-text-secondary"}`}
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white rounded-lg border border-surface-border p-4">
                <h3 className="text-sm font-semibold text-text-primary mb-3">C 端展示</h3>
                <div className="space-y-2">
                  {meta.scenario.map((scenario) => (
                    <div key={scenario} className="flex items-center gap-2 rounded-md bg-surface/50 px-3 py-2">
                      <i className="ri-smartphone-line text-brand text-sm" />
                      <span className="text-xs text-text-secondary">{scenario}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg border border-surface-border p-4">
                <h3 className="text-sm font-semibold text-text-primary mb-3">发布历史</h3>
                <div className="space-y-2">
                  {releases.map((release) => (
                    <div key={release.release_id} className="rounded-md bg-surface/50 p-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] font-mono text-text-primary font-medium">{release.version}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${release.status === "active" ? "bg-emerald-500/10 text-emerald-500" : "bg-text-muted/10 text-text-muted"}`}>{release.status}</span>
                      </div>
                      <p className="text-[10px] text-text-muted mt-1">{formatTime(release.published_at)}</p>
                      <p className="text-[11px] text-text-secondary truncate mt-1">{release.description || "协议与合规发布"}</p>
                    </div>
                  ))}
                  {releases.length === 0 && <p className="text-xs text-text-muted">暂无发布历史</p>}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-surface-border p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-text-primary">回滚操作</h3>
                <p className="text-xs text-text-muted mt-0.5">回滚会生成新的后台发布记录；C 端实际读取结果需另行核对。</p>
              </div>
              <button
                onClick={() => fallbackRelease && handleRollback(fallbackRelease.release_id)}
                disabled={!fallbackRelease || !!rolling}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface text-text-secondary hover:bg-amber-500/10 hover:text-amber-500 disabled:opacity-50"
              >
                <i className={rolling ? "ri-loader-4-line animate-spin text-sm" : "ri-history-line text-sm"} />
                {rolling ? "回滚中" : fallbackRelease ? `回滚到 ${fallbackRelease.version}` : "暂无可回滚版本"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
